import { createContext, useContext } from 'react';

import type {
  CartItemType,
  CartTotalsType,
  FreeShippingType,
} from '@zola-helpers/client/dist/es/@types/cart';
import type { WProjectCustomizationFullView, WProjectFullView } from '@zola/svc-web-api-ts-client';
import type { ConvertWebApiDeclaredToActual } from '@zola-helpers/client/dist/es/@types/svc-web-api';

type CardProjectLookup = {
  [projectUuid: string]: ConvertWebApiDeclaredToActual<WProjectFullView>;
};

export type CartContextValue = {
  busy: boolean;
  /** Have cart items loaded */
  initialized: boolean;
  /** Have cart items and cart projects loaded */
  loaded: boolean;
  size: number;
  items: CartItemType[];
  totals: CartTotalsType;
  isFreeShippingEligible: boolean;
  freeShipping: FreeShippingType | null;
  cartId?: string | null;
  registryId?: string | null;
  isHomeStore: boolean;
  isBaby: boolean;

  /** Look up of card projects in the cart by project-uuid */
  cardProjects: CardProjectLookup;
};

export type CartContextMethods = {
  fetchCart: () => Promise<void>;
  removeCartItem: (cartItemId: string) => Promise<void>;
  removeCartItems: (cartItemIds: string[], skipPaperOnboarding?: boolean) => Promise<void>;
  updateCartItemQuantity: (cartItemId: string, quantity: number) => Promise<void>;
  onCheckout: () => void;
  getCardCustomization: (
    cardProjectUuid: string,
    customizationUuid: string
  ) => ConvertWebApiDeclaredToActual<WProjectCustomizationFullView> | null;
  getCardProject: (
    cardProjectUuid: string
  ) => ConvertWebApiDeclaredToActual<WProjectFullView> | null;
};

export type FullCartContextValue = CartContextValue & CartContextMethods;

export const defaultCartContext: FullCartContextValue = {
  busy: false,
  initialized: false,
  loaded: false,
  size: 0, // sometimes we will only have this
  items: [],
  totals: {},
  isFreeShippingEligible: false,
  freeShipping: null,
  isHomeStore: false,
  isBaby: false,
  cardProjects: {} as CardProjectLookup,
  fetchCart: () => Promise.resolve(),
  removeCartItem: () => Promise.resolve(),
  removeCartItems: () => Promise.resolve(),
  updateCartItemQuantity: () => Promise.resolve(),
  onCheckout: () => undefined,
  getCardCustomization: () => null,
  getCardProject: () => null,
};

export const CartContext = createContext<FullCartContextValue | undefined>(undefined);

export const useCartContext = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCartContext must be used within a CartProvider');
  }
  return context;
};
